import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import { KeyboardStickyView } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Text from "@components/CustomText";
import { identifyAttendanceSubject } from "@services/api";
import { ATTENDANCE_ROLES } from "@utils/attendance";
import { ATTENDANCE_COLORS as C } from "./constants";
import type { ApiErrorBody, AttendanceRole, IdentifyResponse } from "./types";

interface Props {
  visible: boolean;
  role: AttendanceRole | null;
  onClose: () => void;
  onIdentified: (identity: IdentifyResponse, phone: string) => void;
}

const PHONE_LENGTH = 10;
const SHEET_PADDING_BOTTOM = 24;
const GENERIC_ERROR = "Something went wrong. Please try again.";

const promptFor = (role: AttendanceRole | null) =>
  role === ATTENDANCE_ROLES.JR_EXECUTIVE
    ? "Provide the phone number registered as DB with CityMall"
    : "Provide the Executive Number registered with CityMall";

const PhoneModal = ({ visible, role, onClose, onIdentified }: Props) => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Edge-to-edge (Android 15 / RN 0.86): the sheet draws under the nav bar,
  // so pad it by the bottom inset, and cancel that padding while the keyboard
  // is up (the keyboard already covers the nav bar area). Same pattern as
  // AddBagNoModal.
  const insets = useSafeAreaInsets();

  useEffect(() => {
    if (visible) {
      setPhone("");
      setError(null);
      setLoading(false);
    }
  }, [visible, role]);

  const canSubmit = phone.length === PHONE_LENGTH && !loading && role !== null;

  const submit = useCallback(async () => {
    if (!canSubmit || !role) return;
    setLoading(true);
    setError(null);
    try {
      const res = await identifyAttendanceSubject({ role, phone });
      if (res?.status === 200 && res.data?.subject_token) {
        onIdentified(res.data as IdentifyResponse, phone);
        return;
      }
      const body = (res?.data ?? {}) as ApiErrorBody;
      setError(body.message || GENERIC_ERROR);
    } catch (_e) {
      setError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  }, [canSubmit, role, phone, onIdentified]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <View style={styles.scrim}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessibilityLabel="Close"
        />
        <KeyboardStickyView offset={{ closed: 0, opened: insets.bottom }}>
          <View
            style={[
              styles.sheet,
              { paddingBottom: SHEET_PADDING_BOTTOM + insets.bottom },
            ]}
          >
            <Text style={styles.prompt}>{promptFor(role)}</Text>
            <View
              style={[styles.inputRow, error ? styles.inputRowError : null]}
            >
              <View style={styles.prefix}>
                <Text style={styles.prefixText}>+91</Text>
              </View>
              <TextInput
                testID="attendance-phone-input"
                style={styles.input}
                value={phone}
                onChangeText={(text) => {
                  setPhone(text.replace(/\D/g, "").slice(0, PHONE_LENGTH));
                  if (error) setError(null);
                }}
                placeholder="Enter number"
                placeholderTextColor={C.placeholder}
                keyboardType="number-pad"
                maxLength={PHONE_LENGTH}
                editable={!loading}
                autoFocus
                returnKeyType="done"
                onSubmitEditing={submit}
                accessibilityLabel="Phone number"
              />
            </View>
            {error ? (
              <Text testID="attendance-phone-error" style={styles.error}>
                {error}
              </Text>
            ) : null}
            <Pressable
              testID="attendance-login"
              accessibilityRole="button"
              accessibilityState={{ disabled: !canSubmit }}
              disabled={!canSubmit}
              onPress={submit}
              style={[styles.button, !canSubmit && styles.buttonDisabled]}
            >
              {loading ? (
                <ActivityIndicator color={C.white} />
              ) : (
                <Text style={styles.buttonText}>Login</Text>
              )}
            </Pressable>
          </View>
        </KeyboardStickyView>
      </View>
    </Modal>
  );
};

export default PhoneModal;

const styles = StyleSheet.create({
  scrim: { flex: 1, backgroundColor: C.scrim, justifyContent: "flex-end" },
  backdrop: { flex: 1 },
  sheet: {
    backgroundColor: C.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
  },
  prompt: { color: C.ink, fontSize: 17, fontWeight: "600", lineHeight: 23 },
  inputRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    overflow: "hidden",
    marginTop: 16,
  },
  inputRowError: { borderColor: C.pending },
  prefix: {
    backgroundColor: C.inputPrefix,
    paddingHorizontal: 14,
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: C.border,
  },
  prefixText: { color: C.ink, fontSize: 16, fontWeight: "600" },
  input: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: C.ink,
  },
  error: { color: C.pending, fontSize: 13, marginTop: 8 },
  button: {
    backgroundColor: C.pending,
    borderRadius: 12,
    paddingVertical: 15,
    alignItems: "center",
    marginTop: 16,
  },
  buttonDisabled: { opacity: 0.4 },
  buttonText: { color: C.white, fontSize: 16, fontWeight: "700" },
});
